import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Calendar, Menu, X, LogIn, LayoutDashboard } from "lucide-react";
import Logo from "./Logo";
import { Button } from "@/components/ui/button";
import InstallPWAButton from "@/components/InstallPWAButton";
import { useBookDemo } from "@/context/BookDemoContext";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
    { to: "/fordele", label: "Fordele", id: "nav-fordele" },
    { to: "/funktioner", label: "Funktioner", id: "nav-funktioner" },
    { to: "/brancher", label: "Brancher", id: "nav-brancher" },
    { to: "/priser", label: "Priser", id: "nav-priser" },
    { to: "/kontakt", label: "Kontakt", id: "nav-kontakt" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { open: openDemo } = useBookDemo();
    const auth = useAuth();
    const isAuthed = !!auth?.user;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (open) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-lg border-b border-slate-200/70 shadow-sm"
                    : "bg-white/70 backdrop-blur-md border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-24 flex items-center justify-between">
                <Logo height={60} className="-ml-2 sm:-ml-8" />

                <nav className="hidden lg:flex items-center gap-1" aria-label="Hovednavigation">
                    {NAV_ITEMS.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.id}
                            className={({ isActive }) =>
                                `px-3.5 py-2 text-sm font-medium rounded-full transition-colors ${
                                    isActive
                                        ? "text-blue-700 bg-blue-50"
                                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    {isAuthed ? (
                        <Link
                            to="/dashboard"
                            data-testid="header-dashboard-link"
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                        >
                            <LayoutDashboard className="w-4 h-4" />
                            Dashboard
                        </Link>
                    ) : (
                        <Link
                            to="/login"
                            data-testid="header-login-link"
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                        >
                            <LogIn className="w-4 h-4" />
                            Log ind
                        </Link>
                    )}
                    <InstallPWAButton className="rounded-full px-3.5 h-10 border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm" label="Hent app" />
                    <Button
                        data-testid="header-book-demo-btn"
                        onClick={openDemo}
                        className="rounded-full px-4 h-10 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md shadow-blue-500/25 hover:shadow-lg transition-all text-sm"
                    >
                        <Calendar className="w-4 h-4 mr-1.5" />
                        Book gratis demo
                    </Button>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Luk menu" : "Åbn menu"}
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
                    <div className="px-4 py-5 space-y-1 max-w-7xl mx-auto">
                        {NAV_ITEMS.map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                data-testid={`${item.id}-mobile`}
                                onClick={() => setOpen(false)}
                                className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                            >
                                {item.label}
                            </Link>
                        ))}
                        {isAuthed ? (
                            <Link
                                to="/dashboard"
                                data-testid="mobile-dashboard-link"
                                onClick={() => setOpen(false)}
                                className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                            >
                                Dashboard
                            </Link>
                        ) : (
                            <Link
                                to="/login"
                                data-testid="mobile-login-link"
                                onClick={() => setOpen(false)}
                                className="block px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                            >
                                Log ind
                            </Link>
                        )}
                        <div className="pt-1">
                            <InstallPWAButton className="w-full rounded-xl h-12 border border-slate-200 text-slate-700 justify-center" label="Hent app" />
                        </div>
                        <div className="pt-3">
                            <Button
                                data-testid="mobile-book-demo-btn"
                                onClick={() => { setOpen(false); openDemo(); }}
                                className="w-full rounded-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                            >
                                <Calendar className="w-4 h-4 mr-2" />
                                Book gratis demo
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
